import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../../types';
import { createTask, searchPatients } from '../../firebase';
import { createPortal } from 'react-dom';

interface TasksSectionProps {
  user: User;
  todayTasks: any[];
  pendingTasks: any[];
  allTasks: any[];
  isTasksLoading: boolean;
  handleTaskStatusChange: (taskId: string, status: 'pending' | 'inProgress' | 'completed') => Promise<void>;
  handleDeleteTask: (taskId: string) => void;
  handlePatientSelect: (caseId: string) => void;
  refreshTasks: () => void;
}

export const TasksSection: React.FC<TasksSectionProps> = ({
  user,
  todayTasks,
  pendingTasks,
  allTasks,
  isTasksLoading,
  handleTaskStatusChange,
  handleDeleteTask,
  handlePatientSelect,
  refreshTasks
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [viewMode, setViewMode] = useState<'today' | 'pending' | 'all'>('today');
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    dueDate: new Date().toISOString().split('T')[0],
    caseId: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [caseSearchTerm, setCaseSearchTerm] = useState('');
  const [caseSearchResults, setCaseSearchResults] = useState<any[]>([]);
  const [isSearchingCases, setIsSearchingCases] = useState(false);
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>(null);

  const formatDate = (date: any) => {
    if (!date) return '';
    try {
      if (date.toDate && typeof date.toDate === 'function') {
        return new Date(date.toDate()).toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch (e) {
      return '';
    }
  };

  // Handle case search
  const handleCaseSearch = async (searchTerm: string) => {
    setCaseSearchTerm(searchTerm);
    
    if (searchTerm.length < 2) {
      setCaseSearchResults([]);
      setShowCaseDropdown(false);
      return;
    }

    setIsSearchingCases(true);
    setShowCaseDropdown(true);
    try {
      const results = await searchPatients(searchTerm);
      console.log('Patient search results:', results);
      // Map the results to the format we need
      const mappedResults = results.map((patient: any) => {
        const firstName = patient.firstName || patient.first_name || '';
        const lastName = patient.lastName || patient.last_name || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ') || patient.email || patient.caseId || patient.case_id;
        // IMPORTANT: Use case_id from PostgreSQL API response
        const caseId = patient.case_id || patient.caseId || patient.id;
        
        console.log('Mapped patient:', { fullName, caseId, originalPatient: patient });
        
        return {
          id: caseId,
          caseId: caseId,
          title: fullName,
          subtitle: caseId ? `Case: ${caseId}` : undefined
        };
      });
      setCaseSearchResults(mappedResults);
    } catch (error) {
      console.error('Error searching cases:', error);
      setCaseSearchResults([]);
    } finally {
      setIsSearchingCases(false);
    }
  };

  // Handle case selection
  const handleSelectCase = (patient: any) => {
    console.log('===== PATIENT SELECTION =====');
    console.log('Selected patient:', patient);
    console.log('Patient caseId:', patient.caseId);
    console.log('Patient id:', patient.id);
    console.log('=============================');
    
    setSelectedCase(patient);
    setTaskForm({ ...taskForm, caseId: patient.caseId || patient.id });
    setCaseSearchTerm(patient.title || patient.caseId || patient.id);
    setShowCaseDropdown(false);
    setCaseSearchResults([]);
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!showTaskDialog) {
      setCaseSearchTerm('');
      setCaseSearchResults([]);
      setSelectedCase(null);
      setShowCaseDropdown(false);
    }
  }, [showTaskDialog]);

  // Handle add task
  const handleAddTask = async () => {
    if (!taskForm.title.trim()) {
      return;
    }

    // Debug: Show what caseId we're about to save
    if (taskForm.caseId) {
      console.log('===== TASK DEBUG =====');
      console.log('Task will be saved with caseId:', taskForm.caseId);
      console.log('Selected case object:', selectedCase);
      console.log('======================');
    }

    try {
      setIsSaving(true);
      
      const result = await createTask({
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        dueDate: taskForm.dueDate,
        caseId: taskForm.caseId || undefined,
        patientName: selectedCase?.title || undefined
      }, user?.email || user?.id || 'unknown');

      if (result.success) {
        console.log('✅ Task created successfully with ID:', result.taskId);
        console.log('✅ Task was assigned to caseId:', taskForm.caseId);
        
        // Show success alert with the case ID
        alert(`Task created and assigned to case: ${taskForm.caseId}`);
        
        setShowTaskDialog(false);
        setTaskForm({
          title: '',
          description: '',
          priority: 'medium',
          dueDate: new Date().toISOString().split('T')[0],
          caseId: ''
        });
        setSelectedCase(null);
        setTimeout(() => {
          refreshTasks();
        }, 500);
      } else {
        console.error('❌ Task creation failed:', result.error);
        alert('Failed to create task');
      }
    } catch (error) {
      console.error('❌ Error creating task:', error);
      alert('Error creating task');
    } finally {
      setIsSaving(false);
    }
  };

  const getDisplayTasks = () => {
    switch (viewMode) {
      case 'today':
        return todayTasks;
      case 'pending':
        return pendingTasks;
      case 'all':
        return allTasks;
      default:
        return [];
    }
  };

  const displayTasks = getDisplayTasks();

  if (isTasksLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          {t('common.loading')}...
        </div>
      </div>
    );
  }

  return (
    <div className="tasks-section" style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h1 style={{ fontSize: '24px', marginBottom: '0', color: '#000' }}>
          {t('navigation.tasks') || 'Tasks'}
        </h1>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowTaskDialog(true)}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              background: '#28a745',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            + {t('common.add') || 'Add'} {t('search.task') || 'Task'}
          </button>

          <button
            onClick={() => setViewMode('today')}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #ced4da',
              background: viewMode === 'today' ? '#007acc' : 'white',
              color: viewMode === 'today' ? 'white' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📋 {t('dashboard.todayTasks') || 'Today\'s Tasks'} ({todayTasks.length})
          </button>
          <button
            onClick={() => setViewMode('pending')}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #ced4da',
              background: viewMode === 'pending' ? '#007acc' : 'white',
              color: viewMode === 'pending' ? 'white' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ⏳ {t('pendingTasks') || 'Pending Tasks'} ({pendingTasks.length})
          </button>
          <button
            onClick={() => setViewMode('all')}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #ced4da',
              background: viewMode === 'all' ? '#007acc' : 'white',
              color: viewMode === 'all' ? 'white' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📊 {t('allTasks') || 'All Tasks'} ({allTasks.length})
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <div style={{ 
        display: 'grid', 
        gap: '12px',
        maxWidth: '1200px'
      }}>
        {displayTasks.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'white',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
            <h3 style={{ color: '#666', marginBottom: '8px' }}>
              {t('common.noTasks') || 'No tasks found'}
            </h3>
          </div>
        ) : (
          displayTasks.map((task: any, idx: number) => (
            <div
              key={task.id || idx}
              style={{
                background: 'white',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                cursor: task.caseId ? 'pointer' : 'default',
                borderLeft: isRTL ? 'none' : `4px solid ${task.status === 'completed' ? '#4caf50' : task.status === 'in_progress' ? '#ff9800' : '#dc3545'}`,
                borderRight: isRTL ? `4px solid ${task.status === 'completed' ? '#4caf50' : task.status === 'in_progress' ? '#ff9800' : '#dc3545'}` : 'none'
              }}
              onClick={() => task.caseId && handlePatientSelect(task.caseId)}
              onMouseEnter={(e) => {
                if (task.caseId) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (task.caseId) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                }
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '16px', 
                    fontWeight: '600',
                    color: '#000',
                    textDecoration: task.status === 'completed' ? 'line-through' : 'none'
                  }}>
                    {task.title}
                  </h4>
                  {task.description && (
                    <p style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '14px', 
                      color: '#666',
                      lineHeight: '1.5'
                    }}>
                      {task.description}
                    </p>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    flexWrap: 'wrap',
                    fontSize: '13px',
                    color: '#666'
                  }}>
                    {task.patientName && (
                      <span>👤 {task.patientName}</span>
                    )}
                    {task.dueDate && (
                      <span>📅 {formatDate(task.dueDate)}</span>
                    )}
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: task.status === 'completed' ? '#e8f5e9' : task.status === 'in_progress' ? '#fff3e0' : '#ffebee',
                      color: task.status === 'completed' ? '#2e7d32' : task.status === 'in_progress' ? '#e65100' : '#c62828',
                      fontWeight: '500'
                    }}>
                      {task.status === 'completed' ? '✓ ' + (t('completed') || 'Completed') : 
                       task.status === 'in_progress' ? '↻ ' + (t('inProgress') || 'In Progress') : 
                       '○ ' + (t('pending') || 'Pending')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTask(task.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#ffebee'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  title={t('common.delete') || 'Delete'}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Task Dialog */}
      {showTaskDialog && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowTaskDialog(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 30px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {t('common.add') || 'Add'} {t('search.task') || 'Task'}
              </h3>
              <button
                onClick={() => setShowTaskDialog(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px 30px', overflowY: 'auto', flex: 1 }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  {t('common.title') || 'Title'} *
                </label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('common.enterTitle') || 'Enter task title'}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  {t('common.description') || 'Description'}
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('common.enterDescription') || 'Enter task description'}
                />
              </div>

              <div style={{ marginBottom: '16px', position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  {t('common.patient') || 'Patient'} ({t('common.optional') || 'Optional'})
                </label>
                <input
                  type="text"
                  value={caseSearchTerm}
                  onChange={(e) => handleCaseSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('common.searchCase') || 'Search for case ID...'}
                />
                {showCaseDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    {isSearchingCases ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        {t('common.loading') || 'Loading...'}
                      </div>
                    ) : caseSearchResults.length > 0 ? (
                      caseSearchResults.map((patient: any) => (
                        <div
                          key={patient.caseId || patient.id}
                          onClick={() => handleSelectCase(patient)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>{patient.title}</div>
                          {patient.subtitle && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                              {patient.subtitle}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                        {t('search.noResults') || 'No results found'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                    {t('common.priority') || 'Priority'}
                  </label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as any })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="low">{t('common.low') || 'Low'}</option>
                    <option value="medium">{t('common.medium') || 'Medium'}</option>
                    <option value="high">{t('common.high') || 'High'}</option>
                    <option value="urgent">{t('common.urgent') || 'Urgent'}</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                    {t('common.dueDate') || 'Due Date'}
                  </label>
                  <input
                    type="date"
                    value={taskForm.dueDate}
                    onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{
              padding: '20px 30px',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowTaskDialog(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleAddTask}
                disabled={!taskForm.title.trim() || isSaving}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: !taskForm.title.trim() || isSaving ? '#ccc' : '#28a745',
                  color: 'white',
                  cursor: !taskForm.title.trim() || isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.3s ease'
                }}
              >
                {isSaving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

